import { TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import { startCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { useShipmentReport } from '../../entities/reports/queries';
import { TCustomReportEntity } from '../../generated/graphql';
import { ReportActions } from './ReportActions';
import { companyFilter, monthFilter } from './reportFilters';

import './ReportPage.scss';

type TRow = {
  companyId: string;
  companyName: string;
  counts: Record<string, number>;
  total: number;
  giftCount: number;
  shipmentCost: number;
  clientCost: number;
  isTotal?: boolean;
};

//const money = new Intl.NumberFormat('en-CA', { currency: 'CAD', style: 'currency' });

export const ShipmentsReport = () => {
  const { filtersPageMode } = useFilter();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [companyId, setCompanyId] = useState<string>();
  const { data: rows, error: reportError, loading: reportLoading } = useShipmentReport(month, companyId);
  const { data: companies, error: companiesError, loading: companiesLoading } = useQueryListAllCompaniesOptions();

  useEffect(() => {
    filtersPageMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statuses = useMemo(() => [...new Set(rows.map((row) => row.status))].sort(), [rows]);
  const companyRows = useMemo(() => {
    const companies = new Map<string, TRow>();
    const total: TRow = { companyId: '', companyName: 'TOTAL', counts: Object.create(null), total: 0, giftCount: 0, shipmentCost: 0, clientCost: 0, isTotal: true };

    for (const row of rows) {
      const company = companies.get(row.companyId) || { companyId: row.companyId, companyName: row.companyName, counts: Object.create(null), total: 0, giftCount: 0, shipmentCost: 0, clientCost: 0 };
      company.counts[row.status] = (company.counts[row.status] || 0) + 1;
      company.total += 1;
      company.giftCount += row.giftCount;
      company.shipmentCost += row.shipmentCost;
      company.clientCost += row.clientCost;

      companies.set(row.companyId, company);
      total.counts[row.status] = (total.counts[row.status] || 0) + 1;
      total.total += 1;
      total.giftCount += row.giftCount;
      total.shipmentCost += row.shipmentCost;
      total.clientCost += row.clientCost;
    }

    return companies.size ? [...companies.values(), total] : [];
  }, [rows]);
  const sheet = useMemo(
    () => [
      ['Month', month],
      ['Company ID', 'Company', ...statuses.map(startCase), 'Gifts', 'Shipment Cost', 'Client Cost', 'TOTAL'],
      ...companyRows.map((row) => [row.companyId, row.companyName, ...statuses.map((type) => row.counts[type] || 0), row.giftCount, row.shipmentCost, row.clientCost, row.total])
    ],
    [month, statuses, companyRows]
  );
  const activeCompanies = useMemo(() => companies.filter((company) => company.active), [companies]);
  const loading = reportLoading || companiesLoading;
  const error = reportError || companiesError;

  const filters = useMemo<TFiltersProps['filters']>(() => [monthFilter(month, setMonth), companyFilter(activeCompanies, companyId, setCompanyId)], [month, activeCompanies, companyId]);

  return (
    <TablePage dataLoading={loading} error={error} title='Shipments (monthly)'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TRow>
          enableSorting={true}
          defaultSorting={{ id: 'companyName', desc: false }}
          columns={[
            {
              accessorKey: 'companyId',
              // Undefined sort values keep the total last in either sort direction.
              accessorFn: (row) => (row.isTotal ? undefined : row.companyId),
              sortUndefined: 'first',
              cell: ({ row }) => (
                <div className='uuid-label' title={row.original.companyId}>
                  {row.original.companyId}
                </div>
              ),
              header: 'Company ID'
            },
            {
              accessorKey: 'companyName',
              accessorFn: (row) => (row.isTotal ? undefined : row.companyName),
              sortUndefined: 'first',
              cell: ({ row }) => row.original.companyName,
              header: 'Company'
            },
            ...statuses.map((type) => ({
              id: `status:${type}`,
              accessorFn: (row: TRow) => (row.isTotal ? undefined : row.counts[type] || 0),
              sortUndefined: 'first' as const,
              cell: ({ row }: { row: { original: TRow } }) => row.original.counts[type] || 0,
              header: startCase(type)
            })),
            {
              accessorKey: 'giftCount',
              accessorFn: (row) => (row.isTotal ? undefined : row.giftCount),
              sortUndefined: 'first',
              cell: ({ row }) => row.original.giftCount,
              header: 'Gifts'
            },
            /*{
              accessorKey: 'shipmentCost',
              accessorFn: (row) => (row.isTotal ? undefined : row.shipmentCost),
              sortUndefined: 'first',
              cell: ({ row }) => money.format(row.original.shipmentCost),
              header: 'Shipment Cost'
            },
            {
              accessorKey: 'clientCost',
              accessorFn: (row) => (row.isTotal ? undefined : row.clientCost),
              sortUndefined: 'first',
              cell: ({ row }) => money.format(row.original.clientCost),
              header: 'Client Cost'
            },*/
            {
              accessorKey: 'total',
              accessorFn: (row) => (row.isTotal ? undefined : row.total),
              sortUndefined: 'first',
              cell: ({ row }) => row.original.total,
              header: 'TOTAL'
            }
          ]}
          data={companyRows}
          emptyText='No shipments were found.'
          getRowId={(row) => (row.isTotal ? 'total' : row.companyId)}
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          hideSubRowsSelect={true}
          hoverableRows={true}
          selectableRows={false}
          virtual={false}
        />
      </div>
      <ReportActions data={sheet} disabled={loading || !!error || rows.length === 0} filename={`cs-shipments-report_${month}`} customReportEntity={TCustomReportEntity.Shipments} />
    </TablePage>
  );
};
